import { useState, useContext } from 'react';
import { FormRule } from 'antd';
import { Form, Modal, LocaleProvider, Typography } from 'iglooform';
import { history } from 'umi';
import { useIntl } from 'react-intl';
import { images } from '@/assets/images';
import { SuccessFilled } from 'iglooicon';
import { agentOnboard } from './service';
import './index.less';
import moment from 'moment';
import _ from 'lodash';
import Context from '@/utils/context';
import { updateRequest } from '@/service/request';
import useEmailVerification from '@/components/email-verification/useEmailVerification';
import {
  reg,
  emailReg,
  phoneNumberReg,
  referralReg,
  idNumberReg,
  nameReg,
} from '@/utils/regexp';

const OnboardingPage = () => {
  const form = Form.useForm();
  const { search } = window.location;
  const { formatMessage } = useIntl();
  const { isPc } = useContext(Context);
  const [passwordSuccess, setPasswordSuccess] = useState<boolean>(false);

  const currentLang = !localStorage.getItem('selected_lang')?.length
    ? 'vi-VN'
    : (localStorage.getItem('selected_lang') as string);

  const {
    emailVerifiSuccess,
    emailExtra,
    emailSuffix,
    emailExtraLabel,
    onEmailChange,
  } = useEmailVerification('agency');

  const handleSubmit = _.throttle(
    () => {
      // 先看是否验证了邮箱
      if (!emailVerifiSuccess) {
        Modal.confirm({
          title: formatMessage({
            id: 'Verify',
            defaultMessage: 'Verify',
          }),
          content: formatMessage({
            id: 'Please verify your Email first.',
            defaultMessage: 'Please verify your Email first.',
          }),
          okText: formatMessage({
            id: 'Got it!',
            defaultMessage: 'Got it!',
          }),
          cancelButtonProps: {
            style: {
              display: 'none',
            },
          },
        });
        return;
      }

      const details = form.getFieldsValue();
      const params = {
        address: details.address && details.address.trim(),
        email: details.email && details.email.trim(),
        id_number: details.id_number && details.id_number.trim(),
        last_name: details.lastName.trim(),
        first_name: details.firstName.trim(),
        password: details.password,
        id_issued_date: new Date(details.issuedDate),
        phone_number: details.phone_number && details.phone_number.trim(),
        referral_code: details.referralCode && details.referralCode.trim(),
        date_of_birth: new Date(details.dateOfBirth),
        subscription: Boolean(details.subscription),
        gender: details.gender,
        province: details.province.trim(),
        email_verified: true,
      };

      updateRequest(localStorage.getItem('selected_lang') || 'vi-VN');
      agentOnboard(params).then((res) => {
        if (res) {
          localStorage.setItem('onboarding_phone_number', params.phone_number);
          history.push('/onboarding/success' + search);
        }
      });
    },
    500,
    { leading: true, trailing: false },
  );

  return (
    <LocaleProvider currentLang={currentLang as string}>
      <div className="onboarding-layout">
        <img
          className="onboarding-banner"
          src={isPc ? images.onboarding_banner : images.onboarding_banner_h5}
          alt=""
        />
        <div
          className="onboarding-form-box"
          style={isPc ? {} : { width: '90%' }}
        >
          <div className="onboarding-form">
            <Form
              form={form}
              config={{
                type: 'Page',
                elements: [
                  {
                    render: () => (
                      <Typography level="h4" style={{ fontWeight: 700 }}>
                        {formatMessage({
                          id: 'Enter Your Details',
                          defaultMessage: 'Enter Your Details',
                        })}
                      </Typography>
                    ),
                  },
                  {
                    type: 'Input',
                    label: (
                      <span>
                        <Typography level="body1">
                          {formatMessage({ id: 'Referral Code' })}{' '}
                        </Typography>
                        <Typography
                          level="body1"
                          style={{ color: '#999999', fontStyle: 'oblique' }}
                        >
                          {formatMessage({ id: '(Optional)' })}
                        </Typography>
                      </span>
                    ),
                    name: 'referralCode',
                    halfRow: true,
                    required: false,
                    initialValue: '',
                    // showOptional: true,
                    rules: [
                      {
                        validator: (_: FormRule, value: string) => {
                          if (
                            !!value &&
                            (!referralReg.test(value?.trim()) ||
                              value?.trim().length !== 8)
                          ) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Invalid Referral Code',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Last Name',
                      defaultMessage: 'Last Name',
                    }),
                    name: 'lastName',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    // maxLength: 80,
                    rules: [
                      {
                        validator: (rule: any, value: string) => {
                          if (!!value && !nameReg.test(value)) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'This is not a valid Name.',
                                }),
                              ),
                            );
                          } else if (!!value && value.length > 80) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The max length of Last Name should be 80 characters',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'First Name',
                      defaultMessage: 'First Name',
                    }),
                    name: 'firstName',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (rule: any, value: string) => {
                          if (!!value && !nameReg.test(value)) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'This is not a valid Name.',
                                }),
                              ),
                            );
                          } else if (!!value && value.length > 80) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The max length of First Name should be 80 characters',
                                }),
                              ),
                            );
                          } else if (value && value.length > 80) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The max length of last name should be 80 characters',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  // {
                  //   type: 'Input',
                  //   label: formatMessage({
                  //     id: 'First Name',
                  //     defaultMessage: 'First Name',
                  //   }),
                  //   name: 'firstName',
                  //   fullRow: false,
                  //   required: true,
                  //   initialValue: '',
                  //   // maxLength: 80,
                  //   rules: [
                  //     {
                  //       validator: (_: FormRule, value: string) => {
                  //         if (!!value && !nameReg.test(value.trim())) {
                  //           return Promise.reject(
                  //             new Error(
                  //               formatMessage({
                  //                 id: 'This is not a vaild Name.',
                  //               }),
                  //             ),
                  //           );
                  //         } else if (!!value && value.length > 80) {
                  //           return Promise.reject(
                  //             new Error(
                  //               formatMessage({
                  //                 id: 'The max length of first name should be 80 characters',
                  //               }),
                  //             ),
                  //           );
                  //         } else {
                  //           return Promise.resolve();
                  //         }
                  //       },
                  //     },
                  //   ],
                  // },
                  // {
                  //   type: 'Input',
                  //   label: formatMessage({
                  //     id: 'Full Name',
                  //     defaultMessage: 'Full Name',
                  //   }),
                  //   name: 'name',
                  //   fullRow: false,
                  //   required: true,
                  //   initialValue: '',
                  //   rules: [
                  //     {
                  //       validator: (rule: any, value: string) => {
                  //         if (!!value && !value?.trim()) {
                  //           return Promise.reject(
                  //             new Error(
                  //               formatMessage({
                  //                 id: 'This is not a vaild Name.',
                  //               }),
                  //             ),
                  //           );
                  //         } else {
                  //           return Promise.resolve();
                  //         }
                  //       },
                  //     },
                  //   ],
                  // },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Phone Number',
                      defaultMessage: 'Phone Number',
                    }),
                    name: 'phone_number',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (_: FormRule, value: string) => {
                          if (!!value && !phoneNumberReg.test(value)) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Invalid phone number format',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Email',
                      defaultMessage: 'Email',
                    }),
                    name: 'email',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    extra: emailExtra,
                    suffix: emailSuffix,
                    disabled: emailVerifiSuccess,
                    extraLabel: emailExtraLabel,
                    onChange: onEmailChange,
                    rules: [
                      {
                        validator: (_: any, value: string) => {
                          if (!!value && !emailReg.test(value)) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Invalid email format',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    label: formatMessage({
                      id: 'Date of Birth',
                      defaultMessage: 'Date of Birth',
                    }),
                    type: 'DatePicker',
                    name: 'dateOfBirth',
                    fullRow: false,
                    required: true,
                    disabledDate: (current: any) =>
                      current && current > moment().endOf('day'),
                  },
                  {
                    label: formatMessage({
                      id: 'Gender',
                      defaultMessage: 'Gender',
                    }),
                    type: 'Select',
                    name: 'gender',
                    fullRow: false,
                    required: true,
                    options: [
                      { label: formatMessage({ id: 'Male' }), value: 'M' },
                      { label: formatMessage({ id: 'Female' }), value: 'F' },
                    ],
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'ID Number',
                      defaultMessage: 'ID Number',
                    }),
                    name: 'id_number',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (rule: any, value: string) => {
                          if (!!value && !idNumberReg.test(value)) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Invalid ID Number format',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                  },
                  {
                    label: formatMessage({
                      id: 'ID Issued Date',
                      defaultMessage: 'ID Issued Date',
                    }),
                    type: 'DatePicker',
                    name: 'issuedDate',
                    fullRow: false,
                    required: true,
                    disabledDate: (current: any) =>
                      current && current > moment().endOf('day'),
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'City',
                      defaultMessage: 'City',
                    }),
                    name: 'city',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Province',
                      defaultMessage: 'Province',
                    }),
                    name: 'province',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                  },
                  {
                    type: 'Input',
                    label: formatMessage({
                      id: 'Address',
                      defaultMessage: 'Address',
                    }),
                    name: 'address',
                    fullRow: false,
                    required: true,
                    initialValue: '',
                  },
                  {
                    type: 'Divider',
                    label: '',
                  },
                  {
                    render: () => (
                      <Typography level="h4">
                        {formatMessage({
                          id: 'Please Create a Password for Your Account',
                          defaultMessage:
                            'Please Create a Password for Your Account',
                        })}
                      </Typography>
                    ),
                  },
                  {
                    type: 'Password',
                    label: formatMessage({
                      id: 'Enter a Password',
                      defaultMessage: 'Enter a Password',
                    }),
                    name: 'password',
                    fullRow: true,
                    required: true,
                    initialValue: '',
                    extra: [
                      <div key="0">
                        <SuccessFilled
                          className={
                            passwordSuccess
                              ? 'passwordSuccessIcon'
                              : 'passwordUnsuccessIcon'
                          }
                        />
                        <span>
                          {formatMessage({
                            id: 'At least 8 characters long.',
                            defaultMessage: 'At least 8 characters long.',
                          })}
                        </span>
                      </div>,
                      <div key="1">
                        <SuccessFilled
                          className={
                            passwordSuccess
                              ? 'passwordSuccessIcon'
                              : 'passwordUnsuccessIcon'
                          }
                        />
                        <span>
                          {formatMessage({
                            id: 'Contains uppercase & lowercase letter.',
                            defaultMessage:
                              'Contains uppercase & lowercase letter.',
                          })}
                        </span>
                      </div>,
                      <div key="2">
                        <SuccessFilled
                          className={
                            passwordSuccess
                              ? 'passwordSuccessIcon'
                              : 'passwordUnsuccessIcon'
                          }
                        />
                        <span>
                          {formatMessage({
                            id: 'Contains at least one special character.',
                            defaultMessage:
                              'Contains at least one special character.',
                          })}
                        </span>
                      </div>,
                      <div key="3">
                        <SuccessFilled
                          className={
                            passwordSuccess
                              ? 'passwordSuccessIcon'
                              : 'passwordUnsuccessIcon'
                          }
                        />
                        <span>
                          {formatMessage({
                            id: 'Contains numbers.',
                            defaultMessage: 'Contains numbers.',
                          })}
                        </span>
                      </div>,
                    ],
                    rules: [
                      {
                        validator: (_: FormRule, value: string) => {
                          const p = form.getFieldValue('confirmPassword');
                          if ((!!value && !reg.test(value)) || !value) {
                            if (passwordSuccess) {
                              setPasswordSuccess(false);
                            }
                            if (!value) {
                              return Promise.reject();
                            }
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'Invalid Password',
                                }),
                              ),
                            );
                          } else if (!!p && p !== value) {
                            if (!passwordSuccess) {
                              setPasswordSuccess(true);
                            }
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The two passwords are inconsistent',
                                }),
                              ),
                            );
                          } else {
                            value
                              ? setPasswordSuccess(true)
                              : setPasswordSuccess(false);
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                    antdDependencies: ['confirmPassword'],
                  },
                  {
                    type: 'Password',
                    label: formatMessage({
                      id: 'Confirm Password',
                      defaultMessage: 'Confirm Password',
                    }),
                    name: 'confirmPassword',
                    fullRow: true,
                    required: true,
                    initialValue: '',
                    rules: [
                      {
                        validator: (_: FormRule, value: string) => {
                          const p = form.getFieldValue('password');
                          if (!!value && p !== value) {
                            return Promise.reject(
                              new Error(
                                formatMessage({
                                  id: 'The two passwords are inconsistent',
                                }),
                              ),
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ],
                    antdDependencies: ['password'],
                  },
                  {
                    type: 'Confirmation',
                    name: 'subscription',
                    required: false,
                    initialValue: true,
                    message: (
                      <div className="confirmation">
                        {formatMessage({
                          id: 'I would like to receive information about special discounts, promotions and news from Igloo.',
                          defaultMessage:
                            'I would like to receive information about special discounts, promotions and news from Igloo.',
                        })}
                      </div>
                    ),
                  },
                ],
              }}
              onSubmit={handleSubmit}
              locales={{
                submitText: formatMessage({
                  id: 'Register Now',
                  defaultMessage: 'Register Now',
                }),
                validateMessages: {
                  required:
                    '${label} ' +
                    formatMessage({
                      id: 'is required',
                    }),
                },
              }}
            />
            <div className="backLogin">
              {formatMessage({ id: 'Already a user? ' })}
              <a onClick={() => history.push('/login')}>
                {formatMessage({ id: 'Login' })}
              </a>
            </div>
          </div>
        </div>
      </div>
    </LocaleProvider>
  );
};

export default OnboardingPage;
